import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import PageHeader from "../../layout/PageHeader";
import { integrationApi } from "../../../api/integrationApi";
import { SyncLogTable } from "./components/SyncLogTable";
import { SyncedRecordsTable } from "./components/SyncedRecordsTable";
import { formatRelative } from "./utils/format";

const RECORD_TABS = [
  { id: "", label: "All" },
  { id: "patient", label: "Clients" },
  { id: "practitioner", label: "Caregivers" },
  { id: "appointment", label: "Shifts" },
];

const SYNC_DISPLAY_MAX_MS = 12 * 60 * 1000;
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 120;

function isRecentSyncActivity({ syncInProgress, activeSyncStartedAt, syncStartedAtRef }) {
  if (!syncInProgress) return false;
  const startedAt =
    activeSyncStartedAt || syncStartedAtRef?.current || null;
  if (!startedAt) return true;
  return Date.now() - new Date(startedAt).getTime() < SYNC_DISPLAY_MAX_MS;
}

/**
 * Read-only WellSky sync & reports for the current company (B2B).
 * Configuration lives in Super Admin → Agencies (admin.myalbertai.com).
 */
const WellskySyncReport = (props) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncSuccessMessage, setSyncSuccessMessage] = useState("");
  const [syncErrorMessage, setSyncErrorMessage] = useState("");
  const [recordTab, setRecordTab] = useState("");
  const [recordPage, setRecordPage] = useState(1);
  const [recordSearch, setRecordSearch] = useState("");
  const [recordsData, setRecordsData] = useState(null);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const syncPollTimeoutRef = useRef(null);
  const syncPollCancelRef = useRef(false);
  const syncStartedAtRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await integrationApi.companyStatus();
      setData(res);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load WellSky sync status",
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecords = useCallback(async () => {
    if (!data?.linked) return;
    setRecordsLoading(true);
    try {
      const res = await integrationApi.companyRecords({
        page: recordPage,
        limit: 50,
        resourceType: recordTab || undefined,
        q: recordSearch.trim() || undefined,
      });
      setRecordsData(res);
    } catch {
      setRecordsData(null);
    } finally {
      setRecordsLoading(false);
    }
  }, [data?.linked, recordPage, recordTab, recordSearch]);

  useEffect(() => {
    load();
    const id = setInterval(load, 86_400_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    return () => {
      syncPollCancelRef.current = true;
      if (syncPollTimeoutRef.current) {
        clearTimeout(syncPollTimeoutRef.current);
      }
    };
  }, []);

  const stopSyncPolling = useCallback(() => {
    syncPollCancelRef.current = true;
    if (syncPollTimeoutRef.current) {
      clearTimeout(syncPollTimeoutRef.current);
      syncPollTimeoutRef.current = null;
    }
    setSyncInProgress(false);
  }, []);

  const refreshRecords = useCallback(async () => {
    try {
      const recordsRes = await integrationApi.companyRecords({
        page: recordPage,
        limit: 50,
        resourceType: recordTab || undefined,
        q: recordSearch.trim() || undefined,
      });
      if (!syncPollCancelRef.current) {
        setRecordsData(recordsRes);
      }
    } catch {
      /* ignore */
    }
  }, [recordPage, recordSearch, recordTab]);

  const pollForSyncCompletion = useCallback(
    async ({ baselineSyncAt, jobId }) => {
      const baselineMs = baselineSyncAt
        ? new Date(baselineSyncAt).getTime()
        : 0;
      let attempts = 0;

      syncPollCancelRef.current = false;
      syncStartedAtRef.current = new Date().toISOString();
      setSyncInProgress(true);

      const poll = async () => {
        if (syncPollCancelRef.current) return;
        attempts += 1;

        try {
          let jobState = "";
          let jobFailedReason = "";

          if (jobId) {
            try {
              const jobStatus = await integrationApi.companySyncStatus(jobId);
              jobState = jobStatus?.status || "";
              jobFailedReason = jobStatus?.failedReason || "";
            } catch {
              // Job metadata may expire from Redis; fall back to status polling.
            }
          }

          const res = await integrationApi.companyStatus();
          if (syncPollCancelRef.current) return;

          setData(res);

          const newSyncMs = res?.integration?.lastSuccessfulSync
            ? new Date(res.integration.lastSuccessfulSync).getTime()
            : 0;
          const jobDone = jobState === "completed" || jobState === "failed";
          const syncFinished =
            jobState === "completed" ||
            newSyncMs > baselineMs + 1000 ||
            (!res.syncInProgress && attempts > 1);
          const syncFailed = jobState === "failed";

          if (syncFinished) {
            stopSyncPolling();
            syncStartedAtRef.current = null;
            setSyncSuccessMessage("Sync completed. Data refreshed.");
            setTimeout(() => setSyncSuccessMessage(""), 5000);
            if (res?.linked) {
              await refreshRecords();
            }
            return;
          }

          if (syncFailed) {
            stopSyncPolling();
            syncStartedAtRef.current = null;
            setSyncErrorMessage(
              jobFailedReason ||
                res?.integration?.lastError ||
                "Sync failed. Check recent activity below.",
            );
            return;
          }

          const displayExpired =
            syncStartedAtRef.current &&
            Date.now() - new Date(syncStartedAtRef.current).getTime() >=
              SYNC_DISPLAY_MAX_MS;

          if (displayExpired || attempts >= POLL_MAX_ATTEMPTS) {
            stopSyncPolling();
            syncStartedAtRef.current = null;
            setSyncErrorMessage(
              jobDone
                ? "Sync finished but the dashboard could not confirm the new timestamp. Click Refresh."
                : "Sync is taking longer than expected. It may still run in the background — click Refresh to check again.",
            );
            return;
          }

          syncPollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        } catch (err) {
          if (syncPollCancelRef.current) return;
          if (attempts >= POLL_MAX_ATTEMPTS) {
            stopSyncPolling();
            syncStartedAtRef.current = null;
            setSyncErrorMessage(
              err?.response?.data?.message ||
                err?.message ||
                "Unable to check sync status.",
            );
            return;
          }
          syncPollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      };

      poll();
    },
    [refreshRecords, stopSyncPolling],
  );

  useEffect(() => {
    setRecordPage(1);
  }, [recordTab, recordSearch]);

  useEffect(() => {
    if (data?.linked) {
      loadRecords();
    }
  }, [data?.linked, loadRecords]);

  const handleManualUpdate = async () => {
    setSyncing(true);
    setSyncErrorMessage("");
    setSyncSuccessMessage("");
    stopSyncPolling();

    const baselineSyncAt = data?.integration?.lastSuccessfulSync || null;

    try {
      syncStartedAtRef.current = new Date().toISOString();
      const result = await integrationApi.companySyncNow();
      if (result?.alreadyRunning) {
        setSyncSuccessMessage("Sync already running. Updating results…");
      } else {
        setSyncSuccessMessage("Sync started. Updating results…");
      }
      await pollForSyncCompletion({
        baselineSyncAt,
        jobId: result?.jobId,
      });
    } catch (err) {
      setSyncErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to queue sync",
      );
    } finally {
      setSyncing(false);
    }
  };

  const linked = data?.linked;
  const integration = data?.integration;
  const counts = data?.syncedCounts || {};
  const lastSuccessfulSyncAt =
    integration?.lastSuccessfulSync ||
    data?.recentLogs?.find(
      (log) => log?.completedAt && log.status === "success",
    )?.completedAt ||
    null;
  const latestCompletedLog = data?.recentLogs?.find((log) => log?.completedAt);
  const latestLogHadNoChanges =
    latestCompletedLog?.syncType === "delta" &&
    latestCompletedLog?.status === "success" &&
    (latestCompletedLog?.recordsPulled ?? 0) === 0;
  const showSyncing = isRecentSyncActivity({
    syncInProgress: syncInProgress || data?.syncInProgress,
    activeSyncStartedAt: data?.activeSyncStartedAt,
    syncStartedAtRef,
  });

  return (
    <div>
      <PageHeader
        title="WellSky Sync & Reports"
        subtitle="Sync status and results from your WellSky connection"
        activeLocation={props.activeLocation}
        onChangeLocation={props.onChangeLocation}
        companyDisplayName={props.companyDisplayName}
        currentUser={props.currentUser}
        onLogout={props.onLogout}
        onLock={props.onLock}
      />

      {loading ? (
        <p className="text-sm text-slate-500">Loading sync status…</p>
      ) : null}

      {error ? (
        <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      {!loading && !linked ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <p className="text-sm text-slate-700">
            {data?.message ||
              "WellSky is not enabled for your organization."}
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Your administrator can enable WellSky under Super Admin → Agencies →
            View agency → WellSky Integration.
          </p>
        </div>
      ) : null}

      {!loading && linked && integration ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Status", value: integration.status },
              {
                label: "Last sync",
                value: showSyncing
                  ? "Syncing…"
                  : formatRelative(lastSuccessfulSyncAt),
                isLastSyncCard: true,
              },
              { label: "Clients synced", value: counts.patient || 0 },
              { label: "Caregivers synced", value: counts.practitioner || 0 },
              { label: "Shifts synced", value: counts.appointment || 0 },
              { label: "Clock pushes today", value: data.clockPushesToday || 0 },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <p className="text-xs text-slate-500">{card.label}</p>
                <p className="mt-1 text-xl font-semibold capitalize">
                  {card.value}
                </p>
                {card.isLastSyncCard && showSyncing ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Pulling latest data from WellSky. Large agencies can take
                    up to 10 minutes.
                  </p>
                ) : null}
                {card.isLastSyncCard && !showSyncing && latestLogHadNoChanges ? (
                  <p className="mt-1 text-xs text-slate-500">
                    WellSky had no new changes on the last run
                  </p>
                ) : null}
                {card.isLastSyncCard &&
                !showSyncing &&
                lastSuccessfulSyncAt &&
                !latestLogHadNoChanges ? (
                  <p className="mt-1 text-xs text-slate-400">
                    Last completed sync
                  </p>
                ) : null}
              </div>
            ))}
          </div>

          {integration.lastError ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Last sync issue: {integration.lastError}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleManualUpdate}
              disabled={
                syncing || showSyncing || integration.status === "paused"
              }
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Updating…" : "Manually Update"}
            </button>
            <button
              type="button"
              onClick={() => {
                stopSyncPolling();
                syncStartedAtRef.current = null;
                load();
                loadRecords();
              }}
              className="rounded-lg border px-4 py-2 text-sm"
            >
              Refresh
            </button>
          </div>
          {syncSuccessMessage ? (
            <p className="text-sm text-emerald-600">{syncSuccessMessage}</p>
          ) : null}
          {syncErrorMessage ? (
            <p className="text-sm text-red-600">{syncErrorMessage}</p>
          ) : null}

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-semibold text-slate-900">Synced data from WellSky</h2>
            <p className="mt-1 text-xs text-slate-500">
              Full list from WellSky. Linked = in Client Mgmt / Users; Staging only =
              not copied yet.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {RECORD_TABS.map((tab) => (
                <button
                  key={tab.id || "all"}
                  type="button"
                  onClick={() => setRecordTab(tab.id)}
                  className={`rounded-lg px-3 py-1 text-sm ${
                    recordTab === tab.id
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <input
              type="search"
              placeholder="Search name, email, or WellSky ID…"
              value={recordSearch}
              onChange={(e) => setRecordSearch(e.target.value)}
              className="mt-3 w-full max-w-md rounded-lg border px-3 py-2 text-sm"
            />
            <div className="mt-4">
              <SyncedRecordsTable
                records={recordsData?.records || []}
                loading={recordsLoading}
                total={recordsData?.total || 0}
                page={recordsData?.page || recordPage}
                limit={recordsData?.limit || 50}
              />
            </div>
            {recordsData && recordsData.total > recordsData.limit ? (
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={recordPage <= 1}
                  onClick={() => setRecordPage((p) => Math.max(1, p - 1))}
                  className="rounded border px-3 py-1 text-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={
                    recordPage >= Math.ceil(recordsData.total / recordsData.limit)
                  }
                  onClick={() => setRecordPage((p) => p + 1)}
                  className="rounded border px-3 py-1 text-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-semibold text-slate-900">Recent sync activity</h2>
            <p className="mt-1 text-xs text-slate-500">
              Updates automatically while a manual sync is running
            </p>
            <div className="mt-4">
              <SyncLogTable logs={data.recentLogs || []} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WellskySyncReport;
